import { useState, useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  useGetListsQuery,
  useAddItemMutation,
  useRemoveItemMutation,
} from '../services/bookmarksApi';
import { materialsApi } from '../services/materialsApi';
import { showAuthToast } from '../features/uiSlice';
import { trackEvent } from '../utils/analytics';
import styles from '../css/BookmarkButton.module.css';

// variant: 'default' (Watch.js) | 'hero' (HeroCarousel)
// defaultListKey: system_key списка для быстрого добавления при клике (только hero)
const BookmarkButton = ({ materialId, materialTitle, initialBookmark, variant = 'default', defaultListKey = null, onBookmarkChange, onDropdownChange }) => {
  const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [activeBookmark, setActiveBookmark] = useState(initialBookmark || null);
  const wrapperRef = useRef(null);
  const [dropdownAlign, setDropdownAlign] = useState('left');

  const { data: listsData } = useGetListsQuery(undefined, {
    skip: !isAuthenticated,
  });

  const [addItem, { isLoading: adding }] = useAddItemMutation();
  const [removeItem, { isLoading: removing }] = useRemoveItemMutation();

  const lists = listsData?.data || [];
  const isHero = variant === 'hero';

  useEffect(() => {
    setActiveBookmark(initialBookmark || null);
  }, [initialBookmark]);

  const changeOpen = useCallback((value) => {
    setOpen(value);
    onDropdownChange?.(value);
  }, [onDropdownChange]);

  // Закрытие по клику вне и по скроллу (для fixed dropdown)
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        changeOpen(false);
      }
    };
    const handleScroll = () => changeOpen(false);
    if (open) {
      document.addEventListener('mousedown', handleClick);
      if (isHero) window.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [open, isHero, changeOpen]);

  const openDropdown = () => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const spaceRight = window.innerWidth - rect.right;
      setDropdownAlign(spaceRight < 160 ? 'right' : 'left');
    }
    changeOpen(true);
  };

  const setBookmark = (bookmark) => {
    setActiveBookmark(bookmark);
    onBookmarkChange?.(bookmark);
    dispatch(materialsApi.util.invalidateTags(['Materials']));
  };

  // Трекаем только после успешного unwrap(), чтобы не считать неудачные попытки
  const trackBookmark = (name, listName) => {
    trackEvent(name, {
      item_id: materialId,
      item_name: materialTitle,
      list_name: listName,
    });
  };

  const handleListClick = async (list) => {
    const isActive = activeBookmark && activeBookmark.id === list.id;
    try {
      if (isActive) {
        await removeItem({ listId: list.id, materialId }).unwrap();
        setBookmark(null);
        trackBookmark('remove_from_wishlist', list.name);
      } else {
        await addItem({ listId: list.id, materialId }).unwrap();
        setBookmark({ id: list.id, name: list.name, system_key: list.system_key });
        trackBookmark('add_to_wishlist', list.name);
      }
    } catch {
      // ignore
    }
    changeOpen(false);
  };

  const handleRemove = async () => {
    if (!activeBookmark) return;
    try {
      await removeItem({ listId: activeBookmark.id, materialId }).unwrap();
      const listName = activeBookmark.name;
      setBookmark(null);
      trackBookmark('remove_from_wishlist', listName);
    } catch {
      // ignore
    }
    changeOpen(false);
  };

  const handleHeroClick = async () => {
    if (!isAuthenticated) {
      dispatch(showAuthToast('Войдите, чтобы добавлять в закладки'));
      trackEvent('bookmark_unauthorized', { item_id: materialId, placement: 'hero' });
      return;
    }
    if (activeBookmark) {
      open ? changeOpen(false) : openDropdown();
      return;
    }
    // Не в закладках — сразу добавить в defaultListKey
    if (defaultListKey && lists.length > 0) {
      const defaultList = lists.find((l) => l.system_key === defaultListKey);
      if (defaultList) {
        try {
          await addItem({ listId: defaultList.id, materialId }).unwrap();
          setBookmark({ id: defaultList.id, name: defaultList.name, system_key: defaultList.system_key });
          trackBookmark('add_to_wishlist', defaultList.name);
        } catch {
          // ignore
        }
        return;
      }
    }
    openDropdown();
  };

  const handleDefaultClick = () => {
    if (!isAuthenticated) {
      dispatch(showAuthToast('Войдите, чтобы добавлять в закладки'));
      trackEvent('bookmark_unauthorized', { item_id: materialId, placement: 'default' });
      return;
    }
    open ? changeOpen(false) : openDropdown();
  };

  const activeList = activeBookmark ? lists.find((l) => l.id === activeBookmark.id) : null;
  const activeColor = activeList?.color || null;
  const isLoading = adding || removing;

  const buttonLabel = activeBookmark
    ? activeBookmark.name
    : (isHero ? 'Запланировать' : 'В закладки');

  const dropdownContent = (
    <>
      {lists.map((list) => {
        const isActive = activeBookmark && activeBookmark.id === list.id;
        const color = list.color || 'var(--accent-color)';
        return (
          <button
            key={list.id}
            className={`${styles.item} ${isActive ? styles.itemActive : ''}`}
            style={{ '--item-color': color }}
            onClick={() => handleListClick(list)}
          >
            <span className={styles.itemName}>{list.name}</span>
            {isActive && (
              <svg className={styles.check} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            )}
          </button>
        );
      })}
      {activeBookmark && (
        <>
          <div className={styles.divider} />
          <button className={`${styles.item} ${styles.removeItem}`} onClick={handleRemove}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
            <span className={styles.itemName}>Убрать из закладок</span>
          </button>
        </>
      )}
    </>
  );

  return (
    <div className={`${styles.wrapper} ${isHero ? styles.wrapperHero : ''}`} ref={wrapperRef}>
      <button
        className={`${styles.button} ${isHero ? styles.buttonHero : ''} ${activeBookmark ? styles.active : ''}`}
        onClick={isHero ? handleHeroClick : handleDefaultClick}
        disabled={isLoading}
        style={activeColor ? { '--active-color': activeColor } : undefined}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill={activeBookmark ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
        </svg>
        <span className={styles.buttonLabel}>{buttonLabel}</span>
        <span className={styles.chevronDivider} />
        <svg className={styles.chevron} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {/* Dropdown — авторизован */}
      {open && isAuthenticated && (
        <div
          className={styles.dropdown}
          style={dropdownAlign === 'right' ? { left: 'auto', right: 0 } : { left: 0, right: 'auto' }}
        >
          {dropdownContent}
        </div>
      )}
    </div>
  );
};

export default BookmarkButton;
